import React, { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { api } from "aldoo-ra/API"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { MagnifyingGlassIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import AudioPlayer from "./audio-player"
import ItemSelectionPanel from "./item-selection-panel"
import FileBrowser from "./file-browser"
import FileUpload from "./file-upload"

const cmsAPI = api("cms")

function getFileType(file) {
  if (!file) return null
  if (file.type) return file.type
  const url = file.url || file.path || ""
  if (/\.(jpe?g|png|gif|webp|svg|avif)$/i.test(url)) return "image"
  if (/\.(mp4|webm|mov|avi|mkv)$/i.test(url)) return "video"
  if (/\.(mp3|wav|ogg|aac|flac|m4a)$/i.test(url)) return "audio"
  return null
}

function QuickPreview({ file, onClose, zIndex }) {
  const src = file.url || (file.path ? `/${file.path}` : null)
  const type = getFileType(file)

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/75"
      style={{ zIndex }}
      onMouseDown={onClose}
    >
      <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center">
        {type === "image" && (
          <img
            src={src}
            alt={file.name || "preview"}
            className="max-w-[85vw] max-h-[85vh] object-contain rounded-lg shadow-2xl cursor-pointer"
            onMouseDown={onClose}
          />
        )}
        {type === "audio" && (
          <div className="bg-admin_paper rounded-2xl shadow-2xl w-[380px]" onMouseDown={(e) => e.stopPropagation()}>
            <AudioPlayer src={src} name={file.name} />
          </div>
        )}
        {type === "video" && (
          <video
            src={src}
            controls
            autoPlay
            className="max-w-[85vw] max-h-[85vh] rounded-lg shadow-2xl bg-black"
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        {!["image", "audio", "video"].includes(type) && (
          <div className="bg-admin_paper rounded-2xl shadow-2xl p-8 text-admin_text text-center">
            <p className="text-sm">{file.name || "File"}</p>
            <p className="text-xs text-admin_text_secondary mt-1">No preview available</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default function MediaLibraryContent({
  title = "Media Library",
  onFileSelect,
  onClose,
  allowMultiSelect = false,
  defaultFileType = null,
  zIndex,
}) {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [quickPreview, setQuickPreview] = useState(false)
  const selectedFile = selectedFiles[selectedFiles.length - 1] || null
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 })

  const [searchText, setSearchText] = useState("")
  const [selectedTypes, setSelectedTypes] = useState(defaultFileType ? [defaultFileType] : [])
  const [selectedSizes, setSelectedSizes] = useState([])
  const initialQuery = defaultFileType
    ? { deleted: { $ne: true }, type: defaultFileType }
    : { deleted: { $ne: true } }
  const searchQuery = useRef(initialQuery)
  const lastQueryStringRef = useRef(JSON.stringify(initialQuery))
  const debouncedTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (debouncedTimerRef.current) clearTimeout(debouncedTimerRef.current)
    }
  }, [])

  const handleSearchChange = (e) => {
    setSearchText(e.target.value)
    triggerDebouncedSearch()
  }

  const handleTypeSelect = (selectedOptions) => {
    setSelectedTypes(selectedOptions.map((o) => o.value))
    triggerDebouncedSearch()
  }

  const handleSizeSelect = (selectedOptions) => {
    setSelectedSizes(selectedOptions.map((o) => o.value))
    triggerDebouncedSearch()
  }

  const triggerDebouncedSearch = () => {
    if (debouncedTimerRef.current) clearTimeout(debouncedTimerRef.current)
    debouncedTimerRef.current = setTimeout(() => {
      const queryString = JSON.stringify(buildQueryObject())
      if (queryString !== lastQueryStringRef.current) {
        lastQueryStringRef.current = queryString
        setRefreshKey((prev) => prev + 1)
      }
    }, 300)
  }

  const buildQueryObject = () => {
    const match = { deleted: { $ne: true } }
    if (searchText.trim()) match.name = searchText.trim()
    if (selectedTypes.length === 1) match.type = selectedTypes[0]
    else if (selectedTypes.length > 1) match.type = { $in: selectedTypes }
    if (selectedSizes.length === 1) match.size = getSizeQuery(selectedSizes[0])
    else if (selectedSizes.length > 1) match.$or = selectedSizes.map((s) => ({ size: getSizeQuery(s) }))
    return match
  }

  useEffect(() => {
    const query = buildQueryObject()
    if (JSON.stringify(query) !== lastQueryStringRef.current) {
      searchQuery.current = query
      lastQueryStringRef.current = JSON.stringify(query)
    }
  }, [searchText, selectedTypes, selectedSizes, refreshKey])

  const getSizeQuery = (sizeCategory) => {
    switch (sizeCategory) {
      case "small": return { $lt: "1M" }
      case "medium": return { $gte: "1M", $lt: "10M" }
      case "large": return { $gte: "10M" }
      default: return {}
    }
  }

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code !== "Space") return
      const tag = document.activeElement?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA") return
      e.preventDefault()
      if (selectedFile) setQuickPreview((v) => !v)
    }
    window.addEventListener("keydown", onKeyDown, { capture: true })
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true })
  }, [selectedFile])

  const handleFileSelect = (file) => setSelectedFiles([file])

  const handleToggleFile = (file) => {
    setSelectedFiles((prev) => {
      const idx = prev.findIndex((f) => {
        if (f._id && file._id) return f._id === file._id
        if (f.path && file.path) return f.path === file.path
        return false
      })
      if (idx !== -1) return prev.filter((_, i) => i !== idx)
      return [...prev, file]
    })
  }

  const handleReplaceSelection = (files) => setSelectedFiles(files)
  const handleClearSelection = () => setSelectedFiles([])

  const refreshFiles = () => setRefreshKey((prev) => prev + 1)

  const handleUploadComplete = () => {
    refreshFiles()
    showNotification({ message: "Files uploaded successfully", className: "bg-admin_success text-white" })
  }

  const handleDelete = () => {
    if (selectedFiles.length === 0) return
    const isMulti = selectedFiles.length > 1
    const message = isMulti
      ? `Are you sure you want to delete ${selectedFiles.length} files?`
      : `Are you sure you want to delete "${selectedFile.name || selectedFile.path?.split("/").pop() || "this file"}"?`
    showPromptDialog({
      title: "Confirm Delete",
      message,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const paths = selectedFiles.map((f) => f.path || f.url)
          await cmsAPI({ file_delete: { files: paths } })
          setSelectedFiles([])
          refreshFiles()
          showNotification({
            message: isMulti ? `${selectedFiles.length} files deleted successfully` : "File deleted successfully",
            className: "bg-admin_success text-white",
          })
        } catch (error) {
          showNotification({ message: "Failed to delete file", className: "bg-admin_error text-white" })
        }
      },
    })
  }

  const handleCopy = () => {
    if (!selectedFile) return
    const urlToCopy = selectedFile.url || (selectedFile.path ? `${window.location.origin}/${selectedFile.path}` : null)
    if (!urlToCopy) {
      showNotification({ message: "No URL available to copy", className: "bg-admin_error text-white" })
      return
    }
    navigator.clipboard.writeText(urlToCopy)
      .then(() => showNotification({ message: "File URL copied to clipboard", className: "bg-admin_success text-white" }))
      .catch(() => showNotification({ message: "Failed to copy URL", className: "bg-admin_error text-white" }))
  }

  const handleSelect = () => {
    if (selectedFiles.length === 0) return
    setQuickPreview(false)
    if (!onFileSelect) return
    onFileSelect(selectedFiles.length === 1 ? selectedFiles[0] : selectedFiles)
    onClose && onClose()
    showNotification({
      message: selectedFiles.length > 1 ? `${selectedFiles.length} files selected` : "File selected",
      className: "bg-admin_success text-white",
    })
  }

  const fileTypeOptions = [
    { value: "image", name: "Images" },
    { value: "document", name: "Documents" },
    { value: "video", name: "Videos" },
    { value: "audio", name: "Audio" },
  ]

  const sizeOptions = [
    { value: "small", name: "Small (< 1MB)" },
    { value: "medium", name: "Medium (1-10MB)" },
    { value: "large", name: "Large (> 10MB)" },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="relative flex items-center w-full px-6 py-4 border-b border-admin_bordered bg-admin_paper flex-shrink-0">
        <Typography component="h2" variant="admin_h4" className="mr-4 my-0">
          {title}
        </Typography>

        <div className="flex-1 flex items-center space-x-4 mr-8">
          <div className="relative w-[400px]">
            <Input
              variant="admin"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  triggerDebouncedSearch()
                }
              }}
              placeholder="Search"
              icon={
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-admin_text_secondary cursor-pointer"
                  onClick={() => triggerDebouncedSearch()}
                />
              }
              showClearButton
              onClear={() => {
                setSearchText("")
                triggerDebouncedSearch()
              }}
            />
          </div>

          <DropDownSelect
            variant="admin"
            options={fileTypeOptions}
            value={fileTypeOptions.filter((opt) => selectedTypes.includes(opt.value))}
            onChange={handleTypeSelect}
            placeholder="File Type"
            multiSelection={true}
            showItemNamesInSelection={true}
            maxHeight="240px"
            styleOverrides={{ container: "relative inline-block text-left w-[150px]" }}
          />

          <DropDownSelect
            variant="admin"
            options={sizeOptions}
            value={sizeOptions.filter((opt) => selectedSizes.includes(opt.value))}
            onChange={handleSizeSelect}
            placeholder="File Size"
            multiSelection={true}
            showItemNamesInSelection={true}
            maxHeight="200px"
            styleOverrides={{ container: "relative inline-block text-left w-[180px]" }}
          />

          <span className="text-admin_text_secondary text-xs whitespace-nowrap">
            {pagination.totalItems} files
          </span>

          <div className="flex-1" />

          <Button
            variant="admin_primary"
            onClick={() => setIsUploadOpen(true)}
            className="whitespace-nowrap h-10 px-6 flex items-center gap-2"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Upload
          </Button>
        </div>
      </div>

      {/* File Browser */}
      <div className="w-full bg-admin_canvas text-admin_text flex flex-col flex-1 overflow-hidden min-h-0">
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <FileBrowser
            match={searchQuery.current}
            onFileSelect={handleFileSelect}
            onFileConfirm={handleSelect}
            onToggleFile={handleToggleFile}
            onReplaceSelection={handleReplaceSelection}
            onClearSelection={handleClearSelection}
            selectedFile={selectedFile}
            selectedFiles={selectedFiles}
            key={refreshKey}
            isVisible={true}
            onPaginationChange={setPagination}
          />
        </div>

        <div className="flex-shrink-0">
          <ItemSelectionPanel
            selectedFile={selectedFile}
            selectedFiles={selectedFiles}
            onReplace={() => setIsUploadOpen(true)}
            onDelete={handleDelete}
            onCopy={handleCopy}
            onSelect={onFileSelect ? handleSelect : undefined}
            onClose={() => setSelectedFiles([])}
          />
        </div>
      </div>

      {/* Quick Preview portal */}
      {quickPreview && selectedFile && createPortal(
        <QuickPreview file={selectedFile} onClose={() => setQuickPreview(false)} zIndex={99999} />,
        document.body
      )}

      {/* Upload Modal */}
      {isUploadOpen && (
        <Modal
          isOpen={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          variant="admin"
          className="md:absolute w-full sm:w-[500px] md:w-[85%] h-full md:h-auto md:min-h-[85%]"
          closeOnEscape
          closeOnClickOutside
          zIndex={zIndex ? zIndex + 10 : undefined}
        >
          <FileUpload
            variant="admin"
            onClose={() => setIsUploadOpen(false)}
            onUploadComplete={handleUploadComplete}
            defaultFileType={defaultFileType}
          />
        </Modal>
      )}
    </div>
  )
}
